import { useState, useEffect } from 'react';
import { mapPartnerDataToFormDefaults } from '../services/partner';
import type { ILeadsService } from '../services/leads';
import type { FormDefaults } from '../services/formDefaults';
import { storageController } from '../controllers/StorageController';

export type UsePartnerDefaultsResult = {
  partnerDefaults: FormDefaults | null;
  isLoading: boolean;
  error: string | null;
};

export const readPartnerIdFromUrl = (): string | null => {
  return new URLSearchParams(window.location.search).get('partner_id');
};

const PARTNER_SESSION_CLEARED_KEY = 'partner_session_cleared_for';

/**
 * Returning users can land with a partner_id after a previous, unrelated
 * session on this device/browser left them blocked (or mid-way through
 * different answers) in local/session storage. The first time we see a
 * given partner_id in this browsing session, wipe that stale state so the
 * partner-referred flow always starts clean instead of, e.g., routing
 * straight to the blocked hold page.
 *
 * Re-landing with the *same* partner_id (a page refresh mid-flow) does not
 * clear again, so in-progress answers from that same session are preserved.
 */
export const resetStorageForNewPartnerLanding = (partnerId: string | null): void => {
  if (!partnerId) return;
  if (sessionStorage.getItem(PARTNER_SESSION_CLEARED_KEY) === partnerId) return;

  storageController.clearAll();
  sessionStorage.setItem(PARTNER_SESSION_CLEARED_KEY, partnerId);
};

export const usePartnerDefaults = (leadsService: ILeadsService): UsePartnerDefaultsResult => {
  const [partnerDefaults, setPartnerDefaults] = useState<FormDefaults | null>(null);
  const [isLoading, setIsLoading] = useState(() => readPartnerIdFromUrl() !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const partnerId = readPartnerIdFromUrl();
    if (!partnerId) return;

    let cancelled = false;

    leadsService
      .getPartnerPreload(partnerId)
      .then(data => {
        if (cancelled) return;
        setPartnerDefaults(mapPartnerDataToFormDefaults(data));
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load partner preferences. Please fill in the form manually.');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [leadsService]);

  return { partnerDefaults, isLoading, error };
};
